import dynamic from 'next/dynamic';
import Image from 'next/image';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
const AnimatedSection = dynamic(() => import('../Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface DirectusInterfaceData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // buttons
  "button.label",
  "button.href",

  // images
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
] as const;

interface TravellingSectionProps {
  data?: DirectusInterfaceData;
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
}

export default function TravellingSection({
  data,
  zIndex,
  thresholds,
  transitionTime,
  mobileThresholds,
  helperRotation
}: TravellingSectionProps) {
  return (
    <ScreenAwareContent
      supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { y: 0, opacity: 1 },
              above: { y: "100vh", opacity: 1 },
              below: { y: "-100vh", opacity: 1 },
            },
            mobile: {
              visible: { y: 0, opacity: 1 },
              above: { y: 0, opacity: 0 },
              below: { y: "-100vh", opacity: 1 },
            }
          }}
        >
          {/* Background Image */}
          <AnimatedSection
              section="div"
              className="absolute left-0 top-0 w-full h-full inset-0"
              mobileThresholds={[
                mobileThresholds?.[0]?.[0],
                mobileThresholds?.[mobileThresholds.length - 1]?.[1],
              ]}
              variants={{
                desktop: {
                  visible: { opacity: 1, x: 0 },
                  above: { opacity: 1, x:0 },
                  below: { opacity: 1, x: 0 },
                },
                mobile: {
                  visible: { opacity: 1, x: 0 },
                  above: { opacity: 1, x: "-100vw" },
                  below:  { opacity: 1, x: 0 },
                },
              }}
            >
            <Image
              src={data?.image?.image_file?.image?.id && data?.image?.image_file?.image?.filename_download ? getReadableImageUrl(data.image.image_file.image.id, data.image.image_file.image.filename_download) : "/images/travel-bg.jpg"}
              alt={data?.image?.alt || "Travel background"}
              fill
              className="absolute left-0 top-0 inset-0 w-full h-full object-cover"
              quality={90}
              priority
            />
          </AnimatedSection>
          <AnimatedSection
              section="div"
              className="h-screen relative w-full"
              mobileThresholds={[
                mobileThresholds?.[0]?.[0],
                mobileThresholds?.[mobileThresholds.length - 1]?.[1],
              ]}
              variants={{
                desktop: {
                  visible: { opacity: 1, x: 0 },
                  above: { opacity: 1, x:0 },
                  below: { opacity: 1, x: 0 },
                },
                mobile: {
                  visible: { opacity: 1, x: 0 },
                  above: { opacity: 1, x: "100vw" },
                  below:  { opacity: 1, x: 0 },
                },
              }}
            >
            {/* Mobile overlay - moved outside and covers full screen */}
            <div className="absolute left-0 top-0 inset-0 w-full h-full bg-primary/40 lg:hidden" />

            {/* Content Container */}
            <div className="relative h-full w-full flex items-end pb-24 lg:block">
              {/* Overlay/Glass Panel */}
              <AnimatedSection
                className="relative w-full lg:absolute lg:inset-auto lg:left-20 lg:bottom-48 lg:max-w-2xl"
                
                thresholds={[
                  thresholds?.[0]?.[0],
                  thresholds?.[thresholds.length - 1]?.[1],
                ]}
                transitionTime={transitionTime}
                variants={{
                  desktop: {
                    visible: { opacity: 1, x: 0 },
                    above: { opacity: 1, x: "-100vw" },
                    below: { opacity: 1, x: 0 },
                  },
                  mobile: {
                    visible: { opacity: 1, y: 0 },
                    above: { opacity: 1, y: 0 },
                    below: { opacity: 1, y: 0 },
                  },
                }}
              >
                {/* Desktop glass effect */}
                <div>
                  <div className="hidden lg:block absolute left-0 top-0 inset-0 w-full h-full bg-primary/25 backdrop-blur-sm rounded-2xl" />

                  {/* Content */}
                  <div className="relative w-full px-7 lg:p-16">
                    <div className="flex flex-col gap-7 lg:gap-16">
                      {/* Text Content */}
                      <div className="flex flex-col space-y-4 mb-8">
                        <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white">
                          {data?.heading?.text || data?.heading?.title}
                        </h2>
                        <div 
                          className="text-[1rem] font-normal leading-normal lg:text-lg font-greycliff text-white tracking-tight"
                          dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")} 
                        />
                      </div>

                      {/* CTA Button */}
                      <a href={data?.button?.href || "#"} className="w-full lg:w-fit px-6 py-4 lg:px-9 lg:py-3 bg-primary rounded-full inline-block text-center">
                        <span className="text-lg lg:text-xl font-greycliff text-white leading-none tracking-tight">
                          {data?.button?.label}
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </AnimatedSection>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full relative">
          {/* Background Image */}
          <div className="absolute left-0 top-0 w-full h-full inset-0">
            <Image
              src={data?.image?.image_file?.image?.id && data?.image?.image_file?.image?.filename_download ? getReadableImageUrl(data.image.image_file.image.id, data.image.image_file.image.filename_download) : "/images/travel-bg.jpg"}
              alt={data?.image?.alt || "Travel background"}
              fill
              className="absolute left-0 top-0 inset-0 w-full h-full object-cover"
              quality={90}
              priority
            />
          </div>
          <div className="relative w-full py-8 lg:py-16 px-8 lg:px-0">
            {/* Mobile overlay - moved outside and covers full screen */}
            <div className="absolute z-10 left-0 top-0 inset-0 w-full h-full bg-primary/40 xl:hidden" />

            {/* Content Container */}
            <div className="relative z-20 h-full w-full flex items-center">
              {/* Overlay/Glass Panel */}
              {/* Content */}
              <div className="lg:ml-20 lg:max-w-2xl w-full lg:p-16 lg:bg-primary/25 lg:backdrop-blur-sm lg:rounded-2xl">

                <div className="flex flex-col space-y-4 mb-8">
                  <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white">
                    {data?.heading?.text || data?.heading?.title}
                  </h2>
                  <div
                    className="text-[1rem] font-normal leading-normal xl:text-lg font-greycliff text-white tracking-tight"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                  />
                </div>

                {/* CTA Button */}
                <a href={data?.button?.href || "#"} className="w-full xl:w-fit px-6 py-4 xl:px-9 xl:py-3 bg-primary rounded-full inline-block text-center">
                  <span className="text-lg xl:text-xl font-greycliff text-white leading-none tracking-tight">
                    {data?.button?.label}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
